import { __assign } from 'tslib';
import React, { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { Popover } from '../../../popover/esm';

var defaultColors = {"hint":"tooltip__hint_1qyoo","arrow":"tooltip__arrow_1qyoo","tooltip":"tooltip__tooltip_1qyoo"};
require('./../default.css');

var styles = {"component":"tooltip__component_142yz","popper":"tooltip__popper_142yz","hint":"tooltip__hint_142yz","target":"tooltip__target_142yz","overlap":"tooltip__overlap_142yz","inline":"tooltip__inline_142yz"};
require('./../index.css');

var invertedColors = {"tooltip":"tooltip__tooltip_17bo1","arrow":"tooltip__arrow_17bo1","hint":"tooltip__hint_17bo1"};
require('./../inverted.css');

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var DEFAULT_OFFSET = [0, 16];
var TooltipDesktop = function (_a) {
    var children = _a.children, content = _a.content, _b = _a.trigger, trigger = _b === void 0 ? 'hover' : _b, _c = _a.onCloseDelay, onCloseDelay = _c === void 0 ? 300 : _c, _d = _a.onOpenDelay, onOpenDelay = _d === void 0 ? 300 : _d, dataTestId = _a.dataTestId, forcedOpen = _a.open, _e = _a.offset, offset = _e === void 0 ? DEFAULT_OFFSET : _e, position = _a.position, contentClassName = _a.contentClassName, arrowClassName = _a.arrowClassName, popoverClassName = _a.popoverClassName, updatePopover = _a.updatePopover, targetClassName = _a.targetClassName, _f = _a.targetTag, TargetTag = _f === void 0 ? 'div' : _f, zIndex = _a.zIndex, onClose = _a.onClose, onOpen = _a.onOpen, getPortalContainer = _a.getPortalContainer, _g = _a.view, view = _g === void 0 ? 'tooltip' : _g, _h = _a.targetRef, targetRef = _h === void 0 ? null : _h, fallbackPlacements = _a.fallbackPlacements, _j = _a.preventOverflow, preventOverflow = _j === void 0 ? true : _j, _k = _a.availableHeight, availableHeight = _k === void 0 ? false : _k, _l = _a.anchor, anchor = _l === void 0 ? null : _l, _m = _a.colors, colors = _m === void 0 ? 'default' : _m, useAnchorWidth = _a.useAnchorWidth;
    var _o = useState(!!forcedOpen), visible = _o[0], setVisible = _o[1];
    var _p = useState(null), target = _p[0], setTarget = _p[1];
    var contentRef = useRef(null);
    var timer = useRef(0);
    var show = forcedOpen === undefined ? visible : forcedOpen;
    var open = function () {
        if (!show) {
            setVisible(true);
            if (onOpen) {
                onOpen();
            }
        }
    };
    var close = useCallback(function () {
        if (show) {
            setVisible(false);
            if (onClose) {
                onClose();
            }
        }
    }, [onClose, show]);
    var toggle = function () {
        if (show) {
            close();
        }
        else {
            open();
        }
    };
    var clickedOutside = useCallback(function (node) {
        if (target && target.contains(node)) {
            return false;
        }
        if (contentRef.current && contentRef.current.contains(node)) {
            return false;
        }
        return true;
    }, [target]);
    useEffect(function () {
        var handleBodyClick = function (event) {
            var eventTarget = event.target;
            if (clickedOutside(eventTarget)) {
                close();
            }
        };
        document.body.addEventListener('click', handleBodyClick);
        document.body.addEventListener('touchstart', handleBodyClick);
        return function () {
            document.body.removeEventListener('click', handleBodyClick);
            document.body.removeEventListener('touchstart', handleBodyClick);
            clearTimeout(timer.current);
        };
    }, [clickedOutside, close]);
    var handleTargetClick = function () {
        toggle();
    };
    var handleMouseOver = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            open();
        }, onOpenDelay);
    };
    var handleMouseOut = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            close();
        }, onCloseDelay);
    };
    var handleTouchStart = function (event) {
        var eventTarget = event.target;
        clearTimeout(timer.current);
        if (clickedOutside(eventTarget)) {
            timer.current = window.setTimeout(function () {
                close();
            }, onCloseDelay);
        }
        else {
            open();
        }
    };
    var onClickProps = { onClick: handleTargetClick };
    var onHoverProps = {
        onMouseOver: handleMouseOver,
        onMouseOut: handleMouseOut,
        onTouchStart: handleTouchStart,
    };
    var getTargetProps = function () {
        var _a;
        var props = {
            className: cn(styles.target, targetClassName, (_a = {},
                _a[styles.inline] = TargetTag === 'span',
                _a)),
        };
        switch (trigger) {
            case 'click':
                return __assign(__assign({}, props), onClickProps);
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return {};
        }
    };
    var getContentProps = function () {
        var props = {
            ref: contentRef,
            'data-test-id': dataTestId,
            className: cn(styles.component, contentClassName),
        };
        switch (trigger) {
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return props;
        }
    };
    return (React.createElement(Fragment, null,
        React.createElement(TargetTag, __assign({ ref: mergeRefs([targetRef, setTarget]) }, getTargetProps()),
            children.props.disabled && React.createElement("div", { className: styles.overlap }),
            children),
        React.createElement(Popover, { anchorElement: anchor || target, open: show, getPortalContainer: getPortalContainer, arrowClassName: cn(arrowClassName, styles.arrow, colorStyles[colors].arrow), popperClassName: cn(styles.popper, styles[view], colorStyles[colors][view]), className: popoverClassName, offset: offset, withArrow: true, position: position, update: updatePopover, zIndex: zIndex, fallbackPlacements: fallbackPlacements, preventOverflow: preventOverflow, availableHeight: availableHeight, useAnchorWidth: useAnchorWidth },
            React.createElement("div", __assign({}, getContentProps()), content))));
};

export { TooltipDesktop };
